import React, { createContext, useContext, useState } from "react";
import { AiChatSourceReference, AttachmentPreviewTarget } from "../AiChat.types";
import { useAiChatActions, useAiChatState } from "../AiChatContext";

interface AiChatDocumentViewerContextValue {
    activeSource: AiChatSourceReference | null;
    activeCitationKey: string | null;
    activeAttachment: AttachmentPreviewTarget | null;
    isOpen: boolean;

    openCitation: (source: AiChatSourceReference, citationKey: string) => void;
    openAttachmentPreview: (attachment: AttachmentPreviewTarget) => void;
    closeViewer: () => void;
    closeForEntityLinkNavigation: () => void;
    // Clears viewer state without touching the expand toggle. Used by code paths that
    // change the active conversation (new chat, switch conversation): the previous
    // conversation's citation/attachment is no longer relevant.
    clearViewer: () => void;
}

const AiChatDocumentViewerContext = createContext<AiChatDocumentViewerContextValue>({
    activeSource: null,
    activeCitationKey: null,
    activeAttachment: null,
    isOpen: false,
    openCitation: () => {},
    openAttachmentPreview: () => {},
    closeViewer: () => {},
    closeForEntityLinkNavigation: () => {},
    clearViewer: () => {},
});

export function AiChatDocumentViewerProvider({ children }: { children: React.ReactNode }) {
    const { isExpanded } = useAiChatState();
    const { setIsExpanded } = useAiChatActions();
    const [activeSource, setActiveSource] = useState<AiChatSourceReference | null>(null);
    const [activeCitationKey, setActiveCitationKey] = useState<string | null>(null);
    const [activeAttachment, setActiveAttachment] = useState<AttachmentPreviewTarget | null>(null);
    // Tracks whether the panel was expanded before the viewer auto-collapsed it, so closing the
    // viewer can restore the prior state. Cleared on entity-link navigation since the user is
    // leaving the chat entirely.
    const [wasExpandedBeforeCitation, setWasExpandedBeforeCitation] = useState(false);

    function clear() {
        setActiveSource(null);
        setActiveCitationKey(null);
        setActiveAttachment(null);
    }

    function collapseIfExpanded() {
        if (isExpanded) {
            setWasExpandedBeforeCitation(true);
            setIsExpanded(false);
        }
    }

    function openCitation(source: AiChatSourceReference, citationKey: string) {
        if (activeCitationKey === citationKey) {
            closeViewer();
            return;
        }
        collapseIfExpanded();
        setActiveAttachment(null);
        setActiveSource(source);
        setActiveCitationKey(citationKey);
    }

    function openAttachmentPreview(attachment: AttachmentPreviewTarget) {
        collapseIfExpanded();
        setActiveSource(null);
        setActiveCitationKey(null);
        setActiveAttachment(attachment);
    }

    function closeViewer() {
        clear();
        if (wasExpandedBeforeCitation) {
            setIsExpanded(true);
            setWasExpandedBeforeCitation(false);
        }
    }

    function closeForEntityLinkNavigation() {
        if (isExpanded) setIsExpanded(false);
        clear();
        setWasExpandedBeforeCitation(false);
    }

    const isOpen = activeSource !== null || activeAttachment !== null;

    return (
        <AiChatDocumentViewerContext.Provider
            value={{
                activeSource,
                activeCitationKey,
                activeAttachment,
                isOpen,
                openCitation,
                openAttachmentPreview,
                closeViewer,
                closeForEntityLinkNavigation,
                clearViewer: clear,
            }}
        >
            {children}
        </AiChatDocumentViewerContext.Provider>
    );
}

export function useAiChatDocumentViewer() {
    return useContext(AiChatDocumentViewerContext);
}
